import {
  _Scale,
  AnimationItem,
  AnimationKeys,
  CenterScaleAnimation,
  CellAlignment,
  Coords,
  DropShadowConfig,
  GeneralShapeConfig,
  ImageShapeDescriptor,
  IncrementState,
  Scale,
  ShapePosition,
  Translate,
  AlignParameters,
} from '../../../../elements/resource/types/shape.type';
import { GeneralShape } from '../general/general-shape';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { BlurFilter, Container, MIPMAP_MODES, Sprite, Texture } from 'pixi.js';
import { RectangleElement } from '../primitive/rectangle-element';
import { ShapeService } from '../../shape.service';
import {
  ResourceData,
  ResourceType,
} from '../../../../elements/resource/resource.types';
import { IRIToId } from '../../../../util/iri';
import { bulkUpdateFinish } from '../../../store/editor.actions';
import { _screenX, _screenY } from '../../../../services/canvas/canvas.service';
import { RootShape } from '../general/root/root-shape';
export interface ImageShapeConfig extends GeneralShapeConfig {
  base64?: string;
}

export class ImageShape extends GeneralShape<
  ImageShapeDescriptor,
  ImageShapeConfig
> {
  get s3Id() {
    return this.descriptor.s3Id;
  }

  set s3Id(val: string) {
    this.descriptor.s3Id = val;
  }

  getType(): string {
    return 'image-shape';
  }

  get rWidth() {
    return this._width * this._scale.x;
  }

  get rHeight() {
    return this._height * this._scale.y;
  }

  get base64() {
    return this.config?.base64;
  }

  constructor(
    service: ShapeService,
    data: ResourceData<ImageShapeDescriptor>,
    config?: ImageShapeConfig,
  ) {
    super(service, data, config);
    console.log('image-shape', data.literals.descriptor);
    this.type = ResourceType.ImportedImageElement;
    this.initialised = false;
    this.init();
  }

  async init() {
    super.init();
    // -- //
    if (this.config?.base64) {
      this.texture = this.textureFromBase64(this.config.base64);
      this.initImageSprite((w, h) => {
        if (w < h) {
          if (0.5 * this.cs.canvasHeight < h) {
            const ratio = (0.5 * this.cs.canvasHeight) / h;
            this.scale.x = ratio;
            this.scale.y = ratio;
          }
        } else {
          if (0.5 * this.cs.canvasWidth < w) {
            const ratio = (0.5 * this.cs.canvasWidth) / w;
            this.scale.x = ratio;
            this.scale.y = ratio;
          }
        }

        const [finalW, finalH] = [w, h].map(
          val => val * (this.scale.x as number) || 1,
        );

        this.x -= finalW / 2;
        this.y -= finalH / 2;

        this.applyTranslate({
          x: this.x,
          y: this.y,
        });
        this.saveTranslate();
        this.applyScale(this.scale);
        this.saveScale(this.scale);
        this.store.dispatch(bulkUpdateFinish());
        this.rc?.patch(
          {
            width: finalW,
            height: finalH,
          },
          true,
        );
        this.rc?.show();
      });
    } else {
      const id = this.s3Id || IRIToId(this.resourceData.IRI);
      try {
        this.texture = this.textureFromUrl(
          id?.startsWith('http')
            ? id
            : `https://s3.eu-central-1.amazonaws.com/nowords-vlog.illustrations/${id}`,
        );
      } catch (error) {
        console.log('image load error', error);
        return;
      }
      this.initImageSprite();
    }
  }

  resetBaseState(): void {
    this.opacity = 1;
    super.resetBaseState();
  }
  textureFromBase64(base64: string) {
    return Texture.from(base64);
  }
  textureFromUrl(base64: string) {
    const texture = Texture.from(base64, {
      resourceOptions: {
        crossOrigin: 'anonymus',
      },
    });

    if (this.parent instanceof RootShape) {
      this.parent.shapesToBeLoaded[this.resourceData.IRI] = true;
    }

    return texture;
  }

  async initImageSprite(cb?: (width: number, height: number) => void) {
    return new Promise<void>(res => {
      this.texture.baseTexture.mipmap = MIPMAP_MODES.ON;

      this.sprite = new Sprite(this.texture);
      // this.container.visible = false;
      this.container.addChild(this.sprite);

      this.sprite.interactive = true;

      this.sprite.on('pointerdown', () => {
        this.cs.mouseDownAbsorbed = true;
      });
      this.sprite.on('pointerup', () => {
        // this.cs.clickAbsorbed = true;
        this.clicked();
      });
      // this.container.visible = false;

      // this.sprite.on('mouseout', () => this.deselect({ onHover: true }));
      this.sprite.on('mouseover', () => {
        if (!this.cs.shapeAddMode) {
          this.select({ onHover: true });
        }
      });

      if (!this.position.scale) {
        this.position.scale = { x: 1, y: 1 };
      }

      const initAfterLoad = () => {
        this.setWidthHeight();

        if (this.parent instanceof RootShape) {
          delete this.parent.shapesToBeLoaded[this.resourceData.IRI];
        }

        const [width, height] = [this.texture.width, this.texture.height];

        /* console.log(
          'width',
          width,
          'height',
          height,
          'cellAlignment',
          this.cellAlignment,
        ); */
        /* console.warn('------------- init-after-load -------------', {
          width,
          height,
          parent: this.parent,
        }); */

        this.setInitialDims(width, height);
        this.redraw();

        this.scaleImage();
        this.doAlign(this.descriptor.cellAlignment);

        // -- //
        // console.log('text.shape.initial.doAlign', translate);
        // -- //

        this.container.visible = true;
        this.initialised = true;

        this.saveBBox();
        this.initRect();

        if (!this.config?.base64) {
          this.setInitialState();
        }
        cb?.(width, height);
        res();
      };

      if (this.texture.baseTexture.valid) {
        initAfterLoad();
      } else {
        this.texture.baseTexture.on('loaded', initAfterLoad);
      }
    });
  }

  get textureWidth() {
    return this.texture?.width;
  }

  get textureHeight() {
    return this.texture?.height;
  }

  // startTransformation(dx?: number, dy?: number): void {
  //   super.startTransformation(dx, dy);
  //   this._scaleX = this.scaleX;
  //   this._scaleY = this.scaleY;
  // }

  // transformation(scaleX: number, scaleY: number, dx?: number, dy?: number) {
  //   super.transformation(scaleX, scaleY, dx, dy);
  //   this.scaleX = this._scaleX * scaleX;
  //   this.scaleY = this._scaleY * scaleY;
  //   this.redraw();
  // }

  set_BBox() {
    const { width, height } = this.container.getBounds();
    this._bBox = [width, height];
    console.log('is', this._bBox);
  }

  get hasFrameRectangle() {
    return true;
  }

  centerScaleIncrementState: IncrementState<{ width: number; height: number }>;
  _bBox: [number, number];

  prepareKeyValueAnimation(
    animation: AnimationItem<AnimationKeys>,
    division: number,
    duration: number,
  ): void {
    super.prepareKeyValueAnimation(animation, division, duration);
    const { key, value } = animation;
    switch (key) {
      case 'center-scale':
        //
        this.set_BBox();
        const { ratio } = value as CenterScaleAnimation;
        // const { width, height } = this.container.getBounds();
        const [width, height] = this._bBox;
        console.log('prepare-center-scale', { width, height });
        this.centerScaleIncrementState = {
          current: 1,
          increment: (ratio - 1) / division,
          data: { width, height },
        };
        break;
    }
  }

  incrementAnimation(
    increment: number,
    id?: string,
    maxIncrement?: number,
  ): void {
    super.incrementAnimation(increment, id, maxIncrement);
    let current;
    this.getAnimationsById(id).map(({ key }) => {
      switch (key) {
        case 'center-scale':
          // -- // -- // -- //
          current = this.incrementState(
            this.centerScaleIncrementState,
            increment,
          );
          const { width: originalWidth, height: originalHeight } =
            this.centerScaleIncrementState.data;

          this.centerScale([originalWidth, originalHeight], current);
          break;
      }
    });
  }

  centerScale(bBox: Coords, ratio: number): void {
    // super.centerScale(bBox, ratio);
    const [width, height] = bBox;
    const [currentWidth, currentHeight] = [width, height].map(
      val => val * ratio,
    );

    // const [width, height] = bBox;
    // const [currentWidth, currentHeight] = [width, height].map(
    //   val => val * ratio
    // );

    if (this.importedShapeParent) {
      const { x, y } = this.importedShapeParent.scale as _Scale;
      this._redraw({
        x: this.x - (currentWidth - width) / (2 * x),
        y: this.y - (currentHeight - height) / (2 * y),
        scale: { x: this._scale.x * ratio, y: this._scale.y * ratio },
      });
    } else {
      this._redraw({
        x: this.x - (currentWidth - width) / 2,
        y: this.y - (currentHeight - height) / 2,
        scale: { x: this._scale.x * ratio, y: this._scale.y * ratio },
      });
    }

    (this.dropShadowElement as RectangleElement)?.patch({
      width: currentWidth,
      height: currentHeight,
    });
  }

  alignVertically(dims: Coords, align?: 'left' | 'center' | 'right'): number {
    const [, h] = dims;
    return (h - this._height * this._scale.y) / 2;
  }
  scaleToWidth(width: number) {
    // -- //
    console.log('scale-to-width', {
      width,
      _width: this._width,
    });
    if (this._width == width) {
      return;
    }

    this._scale.x = width / this._width;
    this._scale.y = width / this._width;
    this.applyScale(this.scale);
  }

  setInitialDims(width: number, height: number) {
    this._width = width;
    this._height = height;
    // -- //

    // console.log('set-initial-dims', { width, height });
    // console.log('auto-scale', this.descriptor.autoScale);
    // -- // -- //

    if (this.descriptor.autoScale) {
      const [xRatio, yRatio] = [width / _screenX, height / _screenY];

      console.log('xRatio', xRatio, 'yRatio', yRatio);

      let scale: number;
      // -- // -- //
      if (xRatio < yRatio) {
        scale = (0.9 * _screenY) / height;
      } else {
        scale = (0.8 * _screenX) / width;
      }
      this.scale.x = scale;
      this.scale.y = scale;
      this._scale = this.scale as _Scale;

      this.x = (_screenX - scale * width) / 2;
      this.y = (_screenY - scale * height) / 2;
      this.applyTranslate({
        x: this.x,
        y: this.y,
      });
      this.applyScale(this.scale);
    }

    this.rc?.patch({
      width: width * (this.scale.x as number),
      height: height * (this.scale.y as number),
    });
  }

  initRect() {
    if (!this.editable) {
      return;
    }

    this.rc = new RectangleController(
      this,
      {
        offset: [0, 0],
        width: this.rWidth,
        height: this.rHeight,
        fill: true,
        drag: ({ x, y, width: w, height: h }) => {
          this.applyTranslate({
            x,
            y,
          });
          this.applyScale({
            x: w / this._width,
            y: h / this._height,
          });
        },
        endDrag: () => {
          this.saveScale(this.scale);
          this.saveTranslate();
        },
        mouseout: () => {
          if (!this.cs.shapeAddMode) {
            this.deselect({ onHover: true });
          }
        },
        clicked: () => this.clicked(),
      },
      this.circleContainer,
      this.auxCircleContainer,
    );
    this.rc.hide();
  }

  _show() {
    // console.log('is._show');
    super._show();
  }

  _hide() {
    // console.log('is._hide');
    super._hide();
  }

  doAlign(
    cellAlignment: CellAlignment,
    params: AlignParameters = {},
  ): Translate {
    // -- // -- //

    super.doAlign(cellAlignment, params);
    // console.log('image-shape > doAlign', cellAlignment);
    if (!cellAlignment) {
      return;
    }

    const { w: _w, h: _h, x, y, margin } = cellAlignment;
    const [xMargin, yMargin] = [_w, _h].map(val => val * (margin || 0.1));
    const [w, h] = [_w - 2 * xMargin, _h - 2 * yMargin];

    if (this._width == undefined) {
      return;
    }

    // -- //
    let scale = 1;
    let _align: 'horizontal' | 'vertical';

    // console.log('w', w, 'h', h, '_width', this._width, '_height', this._height);
    // console.log('--1--', this._width < w, '---2----', this._height < h);
    if (this._width < w && this._height < h) {
      // upscale
      // console.log('upscale', w / this._width, h / this._height);
      if (w / this._width < h / this._height) {
        scale = w / this._width;
        // console.log('case I.');
        _align = 'horizontal';
      } else {
        scale = h / this._height;
        // console.log('case II.'); //
        _align = 'horizontal';
      }
    } else {
      // downscale
      // console.log('downscale', this._width / w, this._height / h);
      if (this._width / w < this._height / h) {
        // height matters
        scale = h / this._height;
        // console.log('case III.');
        _align = 'horizontal';
      } else {
        scale = w / this._width;
        // console.log('case IV.');
        _align = 'vertical';
      }
    }

    // console.log('scale', scale);

    // if (!noResize) {
    this._scale.x = scale;
    this._scale.y = scale;
    this.applyScale(this._scale);
    //}

    let translate: Translate;
    if (_align == 'horizontal') {
      translate = {
        x: x + (_w - this._width * scale) / 2,
        y: y + yMargin,
      };
      // console.log('translate', translate);
    } else {
      translate = {
        x: x + xMargin,
        y: (_h - this._height * scale) / 2,
      };
    }

    // new RectangleElement(this, this.container, {
    //   position: {
    //     x,
    //     y,
    //   },
    //   width: _w,
    //   height: _h,
    //   stroke: '0xff0000',
    // });

    this.translate = translate;

    // this.sprite.setTransform(
    //   this.translate.x,
    //   this.translate.y,
    //   this._scale.x,
    //   this._scale.y,
    // );
    // this.translate.y = 0;
    this.applyTranslate(this.translate);

    return this.translate;
  }

  getTranslateByAlign(align: CellAlignment) {
    const { w, h, x, y } = align;

    return {
      x: x + (w - this._scale.x * this._width) / 2,
      y: y + (h - this._scale.y * this._height) / 2,
    };
  }

  initDropShadowElement(config: DropShadowConfig) {
    this.blurContainer?.destroy();

    console.log('init-drop-shadow-element', config);

    const { strength, margin, color } = config;
    this.blurContainer = new Container();

    const { width, height } = this.container.getBounds();

    this.dropShadowElement = new RectangleElement(this, this.blurContainer, {
      width: width + 2 * margin,
      height: height + 2 * margin,
      fill: this.getColorValue(color),
    });

    this.container.addChildAt(this.blurContainer, 0);

    this.blurContainer.setTransform(-margin, -margin);
    const filter = new BlurFilter(strength);
    this.dropShadowElement.element.filters = [filter];
  }

  redraw() {
    super.redraw();
    this.container.alpha = this.opacity || 1;
  }

  updateOpacity(opacity: number): void {
    // this.sprite.alpha = opacity;
    this.opacity = opacity;
    // console.log('image-shape > updateOpacity', opacity); //
  }

  set opacity(opacity: number) {
    this.sprite.alpha = opacity;
    this.refresh();
  }

  get opacity() {
    return this.sprite.alpha;
  }

  scaleImage() {
    this.sprite?.setTransform(0, 0, this._scale.x, this._scale.y);
  }

  _redraw(position: ShapePosition): void {
    super._redraw(position);
    if (!position.scale) {
      return;
    }
    console.log('image-shape > _redraw', position.scale.x, position.scale.y);
    this.applyScale(position.scale as _Scale);
  }

  get href() {
    return `${this.service.apiUrl}${this.s3Id}`;
  }

  _scaleX: number;
  _scaleY: number;

  startTransformation(dx?: number, dy?: number): void {
    super.startTransformation(dx, dy);
    this._scaleX = this._scale.x;
    this._scaleY = this._scale.y;
  }

  transformation(scaleX: number, scaleY: number, dx?: number, dy?: number) {
    super.transformation(scaleX, scaleY, dx, dy);
    this.scaleX = this._scaleX * scaleX;
    this.scaleY = this._scaleY * scaleY;

    this.applyScale({
      x: this.scaleX,
      y: this.scaleY,
    });
    this.rc?.patch(
      {
        width: this.scaleX * this._width,
        height: this.scaleY * this._height,
      },
      true,
    );
  }

  _currentScale: Scale;

  applyScale(scale: Scale) {
    // this._currentScale = scale;
    this.scale = scale;
    this.sprite?.setTransform(0, 0, scale.x as number, scale.y as number);
    // this.updateRectangleController(); //
  }

  updateRectangleController(): void {
    this.rc?.patch(
      {
        width: this._width * (this.scale.x as number),
        height: this._height * (this.scale.y as number),
      },
      true,
    );
  }

  endTransformation(): void {
    super.endTransformation();
    this.scale = {
      x: this.scaleX,
      y: this.scaleY,
    };
    this.saveScale(this.scale);
    this.rc?.patch({
      width: this._width * (this.scale.x as number),
      height: this._height * (this.scale.y as number),
    });
    // this.rc?.patch(
    //   {
    //     width: this.width,
    //     height: this.height,
    //   },
    //   true,
    // );
  }
}
